import { useQueries, useQuery } from '@tanstack/react-query';
import type { RelationDef } from '../../models/Models';
import { api } from '../../services/Server';
import type { AnyEntity } from './types';

export const useFkOptions = (path: string | undefined) =>
  useQuery({
    queryKey: ['list', path],
    queryFn: () => api.list<AnyEntity>(path!),
    enabled: !!path,
  });

export interface RelationData {
  items: AnyEntity[];
  isLoading: boolean;
  /** The path whose item shape `items` actually conforms to. */
  targetPath: string;
  /** True when the relation is materialised through a join table. */
  isJoin: boolean;
  /** True when the relation is exposed via a dedicated GET/PUT endpoint. */
  isManaged: boolean;
  /** For join relations: the raw join rows that link `item` to its peers. */
  joinRows: AnyEntity[];
  /** For join / managed relations: every available option on the other side. */
  otherOptions: AnyEntity[];
}

export const useRelationData = (rel: RelationDef, item: AnyEntity): RelationData => {
  // Direct:  load targetPath, filter by fkField === item.id
  // Join:    load joinPath filtered by joinSelfField === item.id, then
  //          resolve those ids against otherPath.
  // Managed: GET managedPath(item.id) directly returns the linked peers;
  //          otherPath is still loaded for the multi-select options.
  const directQuery = useQuery({
    queryKey: ['list', rel.targetPath],
    queryFn: () => api.list<AnyEntity>(rel.targetPath!),
    enabled: !!rel.targetPath,
  });
  const joinQuery = useQuery({
    queryKey: ['list', rel.joinPath],
    queryFn: () => api.list<AnyEntity>(rel.joinPath!),
    enabled: !!rel.joinPath,
  });
  const otherQuery = useQuery({
    queryKey: ['list', rel.otherPath],
    queryFn: () => api.list<AnyEntity>(rel.otherPath!),
    enabled: !!rel.otherPath,
  });
  const managedPath = rel.managedPath?.(item.id);
  const managedQuery = useQuery({
    queryKey: ['managed', managedPath],
    queryFn: () => api.list<AnyEntity>(managedPath!),
    enabled: !!managedPath,
  });

  if (rel.managedPath) {
    return {
      items: managedQuery.data ?? [],
      isLoading: managedQuery.isLoading || otherQuery.isLoading,
      targetPath: rel.otherPath ?? '',
      isJoin: false,
      isManaged: true,
      joinRows: [],
      otherOptions: otherQuery.data ?? [],
    };
  }

  if (rel.targetPath && rel.fkField) {
    const items = (directQuery.data ?? []).filter((d) => d[rel.fkField!] === item.id);
    return {
      items,
      isLoading: directQuery.isLoading,
      targetPath: rel.targetPath,
      isJoin: false,
      isManaged: false,
      joinRows: [],
      otherOptions: [],
    };
  }
  if (rel.joinPath && rel.otherPath) {
    const joinRows = (joinQuery.data ?? []).filter(
      (j) => j[rel.joinSelfField!] === item.id,
    );
    const targetIds = new Set(joinRows.map((j) => j[rel.joinOtherField!] as number));
    const otherOptions = otherQuery.data ?? [];
    const items = otherOptions.filter((o) => targetIds.has(o.id));
    return {
      items,
      isLoading: joinQuery.isLoading || otherQuery.isLoading,
      targetPath: rel.otherPath,
      isJoin: true,
      isManaged: false,
      joinRows,
      otherOptions,
    };
  }
  return {
    items: [],
    isLoading: false,
    targetPath: '',
    isJoin: false,
    isManaged: false,
    joinRows: [],
    otherOptions: [],
  };
};

// ---------------------------------------------------------------------------
// Relation counts (used for tab labels)
// ---------------------------------------------------------------------------

/**
 * Build the per-relation list of queries plus a side-table that records,
 * for each relation, the index of the query that holds the count-bearing
 * list. This replaces the previous manual `qi += 1 | 2` walk.
 *
 * - Direct relation: count comes from filtering the target list by FK.
 * - Join relation:   count comes from filtering the join table by FK
 *                    (we don't need the other-side list to compute counts,
 *                    but it's still prefetched here so opening the tab is
 *                    instant - the cache is shared across hooks).
 */
const buildRelationQueryPlan = (relations: RelationDef[], parentId: number) => {
  const queryDefs: { queryKey: unknown[]; queryFn: () => Promise<AnyEntity[]> }[] = [];
  const plan = relations.map((rel) => {
    const countIdx = queryDefs.length;
    if (rel.managedPath) {
      const path = rel.managedPath(parentId);
      queryDefs.push({
        queryKey: ['managed', path],
        queryFn: () => api.list<AnyEntity>(path),
      });
      // Sentinel: no fkField filter - count is just the array length.
      return { rel, countIdx, fkField: '', isManaged: true };
    }
    if (rel.targetPath) {
      queryDefs.push({
        queryKey: ['list', rel.targetPath],
        queryFn: () => api.list<AnyEntity>(rel.targetPath!),
      });
      return { rel, countIdx, fkField: rel.fkField!, isManaged: false };
    }
    queryDefs.push({
      queryKey: ['list', rel.joinPath],
      queryFn: () => api.list<AnyEntity>(rel.joinPath!),
    });
    queryDefs.push({
      queryKey: ['list', rel.otherPath],
      queryFn: () => api.list<AnyEntity>(rel.otherPath!),
    });
    return { rel, countIdx, fkField: rel.joinSelfField!, isManaged: false };
  });
  return { queryDefs, plan };
};

export const useRelationCounts = (
  relations: RelationDef[] | undefined,
  item: AnyEntity | null,
): Record<string, number> => {
  const { queryDefs, plan } = buildRelationQueryPlan(relations ?? [], item?.id ?? 0);
  const queries = useQueries({ queries: queryDefs });

  if (!relations || !item) return {};

  return Object.fromEntries(
    plan.map(({ rel, countIdx, fkField, isManaged }) => {
      const data = (queries[countIdx]?.data as AnyEntity[] | undefined) ?? [];
      const count = isManaged ? data.length : data.filter((d) => d[fkField] === item.id).length;
      return [rel.key, count];
    }),
  );
};
